import type { WorkerRole } from '../../../core/index.js'
import type { HeatDayPhase, HeatScale, HeatState, HeatTally, HeatmapDay, HeatmapSlot, ShiftTypeKey } from '../../types.js'
import type { TodayFilter } from '../today/todayData.js'

/**
 * The facility heatmap's pure parts (26 October 2026): the colour of each state, how a cell is drawn by where it is in
 * time (solid for worked or under way, striped for ahead, grey for a month not released), and the roll-ups a filtered
 * view needs - judged by the scale the API answers, so a unit or role on its own reads as the API would read it.
 */

export interface HeatSwatch {
  label: string
  /** The solid fill, and the text that reads on it. */
  fill: string
  ink: string
  /** The pale tint the stripes of a day ahead are drawn in. */
  tint: string
}

/**
 * Teal for held, gold for thin, coral and a deep red for short: the ends differ in lightness from the pale middle, so
 * the scale reads without colour too.
 */
export const HEAT_SWATCH: Record<HeatState, HeatSwatch> = {
  covered: { label: 'Covered', fill: '#0C5E74', ink: '#FFFFFF', tint: '#CFE3E9' },
  near: { label: 'Nearly covered', fill: '#7FB3C1', ink: '#0A2830', tint: '#E2EEF2' },
  thin: { label: 'Thin', fill: '#EBD58F', ink: '#0A2830', tint: '#F7EDCB' },
  short: { label: 'Short', fill: '#F26B5B', ink: '#0A2830', tint: '#FCDDD9' },
  critical: { label: 'Critical', fill: '#9E2F24', ink: '#FFFFFF', tint: '#F1C6C0' },
  none: { label: 'Nothing required', fill: '#EEF3F5', ink: '#2C5060', tint: '#EEF3F5' },
}

/** The states a key lists, best to worst. */
export const HEAT_ORDER: readonly HeatState[] = ['covered', 'near', 'thin', 'short', 'critical']

/** How a cell is drawn: worked or under way solid, ahead striped, not released grey. */
export type HeatTime = 'worked' | 'live' | 'ahead' | 'unreleased'

export interface HeatPaint {
  background: string
  color: string
  border: string
}

export function heatPaint(state: HeatState, time: HeatTime): HeatPaint {
  const swatch = HEAT_SWATCH[state]
  if (time === 'unreleased') {
    return { background: '#EEF3F5', color: '#2C5060', border: '2px dashed #A9BCC5' }
  }
  if (time === 'ahead' && state !== 'none') {
    return { background: `repeating-linear-gradient(135deg, ${swatch.tint} 0 5px, #FFFFFF 5px 10px)`, color: '#0A2830', border: `2px dashed ${swatch.fill}` }
  }
  return { background: swatch.fill, color: swatch.ink, border: `2px solid ${swatch.fill}` }
}

export const timeOfDay = (phase: HeatDayPhase): HeatTime => (phase === 'ahead' ? 'ahead' : 'worked')

/** A share held, judged by the API's scale (the API's `ratioState`): a single seat missing always reads `near`. */
export function ratioState(staffed: number, required: number, scale: HeatScale): HeatState {
  if (required <= 0) {
    return 'none'
  }
  if (required - staffed === 1) {
    return 'near'
  }
  const ratio = staffed / required
  return ratio >= 1 ? 'covered' : ratio >= scale.near ? 'near' : ratio >= scale.thin ? 'thin' : ratio >= scale.short ? 'short' : 'critical'
}

export const pctOf = (staffed: number, required: number): string => (required > 0 ? `${Math.round((staffed / required) * 1000) / 10}%` : '—')

export const matchesFilter = (slot: Pick<HeatmapSlot, 'unitId' | 'role'>, filter: TodayFilter): boolean => (filter.unitId === null || slot.unitId === filter.unitId) && (filter.role === null || slot.role === filter.role)

/** Seats required and held over some slots, held counted up to what each slot requires (as the API counts a day). */
export function tallySlots(slots: readonly HeatmapSlot[]): HeatTally {
  return slots.reduce<HeatTally>(
    (sum, slot) => ({
      required: sum.required + slot.required,
      staffed: sum.staffed + Math.min(slot.staffed, slot.required),
      onBoard: sum.onBoard + (slot.phase === 'ended' ? 0 : slot.posted + slot.locked),
      ranShort: sum.ranShort + slot.ranShort,
      uncovered: sum.uncovered + slot.uncovered,
    }),
    { required: 0, staffed: 0, onBoard: 0, ranShort: 0, uncovered: 0 },
  )
}

/** A day narrowed to a unit or role: the API's own reading when nothing is filtered. */
export function filteredDay(day: HeatmapDay, filter: TodayFilter, scale: HeatScale): { state: HeatState; tally: HeatTally; slots: HeatmapSlot[] } {
  if (filter.unitId === null && filter.role === null) {
    return { state: day.state, tally: day, slots: day.slots }
  }
  const slots = day.slots.filter((slot) => matchesFilter(slot, filter))
  const tally = tallySlots(slots)
  return { state: ratioState(tally.staffed, tally.required, scale), tally, slots }
}

const minutesOf = (clock: string): number => {
  const [hours, minutes] = clock.split(':').map(Number)
  return (hours ?? 0) * 60 + (minutes ?? 0)
}

/** Whether a shift's span (which may cross midnight) holds a minute of the facility's day. */
function spans(slot: Pick<HeatmapSlot, 'startTime' | 'endTime'>, minute: number): boolean {
  const start = minutesOf(slot.startTime)
  const end = minutesOf(slot.endTime)
  return end > start ? minute >= start && minute < end : minute >= start || minute < end
}

/** Where a shift of today stands at `now` (`HH:mm` where the facility is). */
export function slotTime(slot: Pick<HeatmapSlot, 'phase' | 'startTime'>, now: string): HeatTime {
  if (slot.phase === 'ended') {
    return 'worked'
  }
  return minutesOf(now) >= minutesOf(slot.startTime) ? 'live' : 'ahead'
}

export interface HeatHour {
  /** "07:00". */
  label: string
  state: HeatState
  time: HeatTime
  staffed: number
  required: number
}

/**
 * The day hour by hour, from the earliest shift's start round the clock: each hour the seats on duty against the seats
 * the shifts covering it require. An hour before now is worked; the hour now is under way.
 */
export function hoursOfDay(slots: readonly HeatmapSlot[], now: string, scale: HeatScale): HeatHour[] {
  if (slots.length === 0) {
    return []
  }
  const first = Math.min(...slots.map((slot) => minutesOf(slot.startTime)))
  const startHour = Math.floor(first / 60)
  const nowMinutes = minutesOf(now)
  const sinceStart = (nowMinutes - startHour * 60 + 1440) % 1440
  return Array.from({ length: 24 }, (_, index) => {
    const minute = ((startHour + index) % 24) * 60
    const covering = slots.filter((slot) => spans(slot, minute))
    const tally = tallySlots(covering)
    const offset = index * 60
    const time: HeatTime = offset + 60 <= sinceStart ? 'worked' : offset <= sinceStart ? 'live' : 'ahead'
    return { label: `${String((startHour + index) % 24).padStart(2, '0')}:00`, state: ratioState(tally.staffed, tally.required, scale), time, staffed: tally.staffed, required: tally.required }
  })
}

/** How far into the ribbon now sits, as a percentage, for the marker. */
export function nowOffsetPct(hours: readonly HeatHour[], now: string): number | null {
  const first = hours[0]
  if (!first) {
    return null
  }
  const since = (minutesOf(now) - minutesOf(first.label) + 1440) % 1440
  return (since / 1440) * 100
}

export const SHIFT_ORDER: readonly ShiftTypeKey[] = ['day', 'evening', 'night']
export const SHIFT_LABEL: Record<ShiftTypeKey, string> = { day: 'Day', evening: 'Evening', night: 'Night' }
const ROLE_ORDER: readonly WorkerRole[] = ['RN', 'LPN', 'CNA']

export interface HeatGridRow {
  unitId: string
  role: WorkerRole
  /** One per shift type the day has, in the day's order; null where the unit needs none of the role on it. */
  cells: (HeatmapSlot | null)[]
  tally: HeatTally
}

/** The day's slots as rows of a unit and role by columns of shift types. */
export function gridOf(slots: readonly HeatmapSlot[]): { shiftTypes: ShiftTypeKey[]; rows: HeatGridRow[] } {
  const shiftTypes = SHIFT_ORDER.filter((key) => slots.some((slot) => slot.shiftType === key))
  const keys = [...new Set(slots.map((slot) => `${slot.unitId}|${slot.role}`))]
  const rows = keys
    .map((key) => {
      const [unitId = '', role = 'CNA'] = key.split('|') as [string, WorkerRole]
      const own = slots.filter((slot) => slot.unitId === unitId && slot.role === role)
      return { unitId, role, cells: shiftTypes.map((shiftType) => own.find((slot) => slot.shiftType === shiftType) ?? null), tally: tallySlots(own) }
    })
    .sort((a, b) => a.unitId.localeCompare(b.unitId) || ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role))
  return { shiftTypes, rows }
}

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`

/** A slot's line under its numbers: what became of the seats it is missing. */
export function slotNote(slot: HeatmapSlot): string {
  if (slot.required === 0) {
    return 'none required'
  }
  const missing = Math.max(0, slot.required - slot.staffed)
  if (missing === 0) {
    return slot.phase === 'ended' ? 'worked in full' : slot.staffed > slot.required ? `${slot.staffed - slot.required} over` : 'held'
  }
  const parts = [
    slot.ranShort > 0 ? `${slot.ranShort} run short` : null,
    slot.locked > 0 ? `${slot.locked} inside the lock` : null,
    slot.posted > 0 ? `${slot.posted} on the board` : null,
    slot.uncovered > 0 ? (slot.phase === 'ended' ? `${slot.uncovered} went unfilled` : `${slot.uncovered} not on the board`) : null,
  ].filter(Boolean)
  return parts.join(' · ') || plural(missing, 'seat') + ' short'
}

export interface CalendarCell {
  date: string
  day: number
}

/** The month's dates in calendar order, with the blanks before the first (weeks start on Sunday). */
export function calendarOf(period: string): (CalendarCell | null)[] {
  const [year, month] = period.split('-').map(Number)
  const first = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1))
  const count = new Date(Date.UTC(year ?? 1970, month ?? 1, 0)).getUTCDate()
  const lead: null[] = Array.from({ length: first.getUTCDay() }, () => null)
  return [...lead, ...Array.from({ length: count }, (_, index) => ({ date: `${period}-${String(index + 1).padStart(2, '0')}`, day: index + 1 }))]
}

/** A day's three shift bars: each shift type's seats across the units (and roles shown), judged on the scale. */
export function shiftBars(slots: readonly HeatmapSlot[], scale: HeatScale): { shiftType: ShiftTypeKey; state: HeatState }[] {
  return SHIFT_ORDER.filter((key) => slots.some((slot) => slot.shiftType === key)).map((shiftType) => {
    const tally = tallySlots(slots.filter((slot) => slot.shiftType === shiftType))
    return { shiftType, state: ratioState(tally.staffed, tally.required, scale) }
  })
}
